import React, { useMemo, useState } from 'react';

const PAYMENT_CHANNELS = [
  { key: '', label: 'Select payment channel' },
  { key: 'Cash', label: 'Cash' },
  { key: 'Pay Later', label: 'Pay Later' },
  { key: 'UPI', label: 'UPI / QR' },
  { key: 'Card', label: 'Card' },
  { key: 'Swipe(CC)', label: 'Credit Card' },
  { key: 'Swipe(DC)', label: 'Debit Card' },
  { key: 'Swipe(FC)', label: 'Food Card' },
  { key: 'PLuxee', label: 'PLuxee' },
  { key: 'QR(HDFC)', label: 'QR (HDFC)' },
];

const money = (value) => `Rs. ${Number(value || 0).toFixed(2)}`;

const normalizeAmount = (value) => {
  const cleaned = String(value || '').replace(/[^\d.]/g, '');
  const parts = cleaned.split('.');
  return parts.length > 2 ? `${parts[0]}.${parts.slice(1).join('')}` : cleaned;
};

const isUpiChannel = (channel) => {
  const value = String(channel || '').toUpperCase();
  return value.includes('UPI') || value.includes('QR');
};

const MultiPaymentModal = ({
  total = 0,
  onCancel,
  onConfirm,
  onPayUpi,
  upiPaid = false,
  paymentLoading = false,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const busy = paymentLoading || submitting;
  const payableTotal = Number(total || 0);
  const [rows, setRows] = useState([{ channel: '', amount: '' }]);

  const paidAmount = useMemo(
    () =>
      rows.reduce(
        (sum, row) => sum + (row.channel && row.channel !== 'Pay Later' ? Number(row.amount || 0) : 0),
        0
      ),
    [rows]
  );
  const remaining = Number((payableTotal - paidAmount).toFixed(2));
  const underpaid = remaining > 0.009;
  const balanceDue = Math.max(remaining, 0);
  const changeDue = Math.max(Math.abs(Math.min(remaining, 0)), 0);
  const pendingAmount = rows.reduce((sum, row) => sum + (row.channel === 'Pay Later' ? Number(row.amount || 0) : 0), 0);
  const hasCredit = pendingAmount > 0;
  const allocatedRemaining = Number((remaining - pendingAmount).toFixed(2));
  const canConfirm = hasCredit ? Math.abs(allocatedRemaining) < 0.009 : !underpaid;
  const hasUpiPayment = rows.some(
    (row) => isUpiChannel(row.channel) && Number(row.amount || 0) > 0
  );

  const getPayments = () =>
    rows
      .map((row) => ({
        channel: row.channel,
        amount: Number(row.amount || 0),
      }))
      .filter((row) => row.channel && row.amount > 0);

  const updateRow = (index, patch) => {
    setRows((prev) =>
      prev.map((row, rowIndex) =>
        rowIndex === index ? { ...row, ...patch } : row
      )
    );
  };

  const removeRow = (index) => {
    setRows((prev) => prev.filter((_, rowIndex) => rowIndex !== index));
  };

  const addRow = () => {
    if (allocatedRemaining <= 0) return;
    setRows((prev) => [
      ...prev,
      { channel: '', amount: allocatedRemaining.toFixed(2) },
    ]);
  };

  const handleConfirm = async () => {
    const payments = getPayments();

    if (busy || !canConfirm || payments.length === 0 || (hasUpiPayment && (!upiPaid || hasCredit))) {
      return;
    }

    setSubmitting(true);
    try { await onConfirm?.({
      payments,
      paidAmount: Number(paidAmount.toFixed(2)),
      pendingAmount: Number(pendingAmount.toFixed(2)),
      changeDue: Number(changeDue.toFixed(2)),
    }); } finally { setSubmitting(false); }
  };

  const handlePayUpi = () => {
    const payments = getPayments();

    if (!canConfirm || payments.length === 0 || !hasUpiPayment || hasCredit) {
      return;
    }

    onPayUpi?.({
      payments,
      paidAmount: Number(paidAmount.toFixed(2)),
    });
  };

  return (
    <div className="responsive-dialog fixed inset-0 z-[9999] flex items-center justify-center bg-black/45 px-3">
      <div className="w-full max-w-lg overflow-hidden rounded-2xl border border-orange-100 bg-white shadow-2xl">
        <div className="bg-[#ff8a00] px-5 py-4">
          <h2 className="text-lg font-bold text-white">Payment</h2>
          <div className="mt-1 text-sm font-semibold text-orange-50">
            Bill Amount: {money(payableTotal)}
          </div>
        </div>

        <div className="space-y-4 p-5">
          <div className="space-y-2">
            {rows.map((row, index) => (
              <div
                key={index}
                className="grid grid-cols-[1fr_1fr_auto_auto] gap-2 rounded-xl border border-gray-200 bg-gray-50 p-2"
              >
                <select
                  aria-label={`Payment channel ${index + 1}`}
                  disabled={busy}
                  value={row.channel}
                  onChange={(e) => updateRow(index, { channel: e.target.value, ...(e.target.value === 'Pay Later' ? { amount: Math.max(0, payableTotal - rows.reduce((sum, other, i) => sum + (i !== index && other.channel ? Number(other.amount || 0) : 0), 0)).toFixed(2) } : {}) })}
                  className="h-10 rounded-lg border border-gray-300 bg-white px-2 text-sm font-semibold outline-none"
                >
                  {PAYMENT_CHANNELS.map((option) => (
                    <option key={option.key} value={option.key}>
                      {option.label}
                    </option>
                  ))}
                </select>

                <input
                  aria-label={`Payment amount ${index + 1}`}
                  disabled={busy}
                  value={row.amount}
                  inputMode="decimal"
                  onChange={(e) =>
                    updateRow(index, { amount: normalizeAmount(e.target.value) })
                  }
                  className="h-10 rounded-lg border border-gray-300 bg-white px-2 text-right text-sm font-semibold outline-none"
                  placeholder="Amount"
                />

                <button
                  type="button"
                  onClick={handlePayUpi}
                  disabled={
                    hasCredit || !isUpiChannel(row.channel) ||
                    Number(row.amount || 0) <= 0 ||
                    !canConfirm ||
                    upiPaid ||
                    busy
                  }
                  className="h-10 rounded-lg bg-green-600 px-3 text-sm font-bold text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:bg-gray-300"
                >
                  {upiPaid && isUpiChannel(row.channel)
                    ? 'Paid'
                    : paymentLoading && isUpiChannel(row.channel)
                      ? 'Wait'
                      : 'Pay'}
                </button>

                <button
                  type="button"
                  onClick={() => removeRow(index)}
                  disabled={rows.length === 1 || busy}
                  className="h-10 rounded-lg border border-gray-300 bg-white px-3 text-sm font-bold text-gray-700 disabled:opacity-40"
                >
                  X
                </button>
              </div>
            ))}
          </div>

          <div className="rounded-xl border border-orange-100 bg-orange-50 p-3 text-sm font-semibold">
            <div className="flex justify-between">
              <span>Paid</span>
              <span>{money(paidAmount)}</span>
            </div>
            <div className={`mt-1 flex justify-between ${underpaid ? 'text-red-700' : 'text-gray-800'}`}>
              <span>{underpaid ? 'Remaining' : 'Balance'}</span>
              <span>{money(underpaid ? balanceDue : changeDue)}</span>
            </div>
          </div>

          {hasCredit && <p className="rounded-lg bg-amber-50 p-3 text-sm font-semibold text-amber-800">Pay Later: {money(pendingAmount)} will remain due against this buyer.</p>}
          {hasCredit && hasUpiPayment && <p role="alert" className="text-sm text-red-600">Pay Later can be combined with cash or card; remove the online payment to continue.</p>}
          {allocatedRemaining > 0.009 ? (
            <button
              type="button"
              disabled={busy}
              onClick={addRow}
              className="w-full rounded-xl border border-orange-300 bg-white px-4 py-2.5 text-sm font-bold text-orange-700 hover:bg-orange-50"
            >
              Add Payment Channel
            </button>
          ) : null}
        </div>

        <div className="flex justify-end gap-2 border-t bg-gray-50 px-5 py-4">
          <button
            type="button"
            disabled={busy}
            onClick={onCancel}
            className="rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-bold text-gray-700 hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!canConfirm || (hasUpiPayment && !upiPaid) || busy}
            className="rounded-xl bg-[#ff8a00] px-5 py-2.5 text-sm font-bold text-white hover:bg-[#e57b00] disabled:cursor-not-allowed disabled:bg-orange-300"
          >
            {hasCredit ? 'Confirm Pay Later' : 'Confirm Payment'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MultiPaymentModal;
